import { useState, useEffect } from 'react';
import { adminCreateCampaign, adminUpdateCampaign, adminDeleteCampaign, adminGetCampaigns } from '../lib/api';
import { Campaign } from '@shared/types';

const ADMIN_PASSWORD_KEY = 'dnd-admin-password';

export default function AdminPage() {
  const [password, setPassword] = useState(() => localStorage.getItem(ADMIN_PASSWORD_KEY) || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  // WHY: Separate from `loading` to avoid disabling form buttons during initial page load
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newLevel, setNewLevel] = useState(1);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLevel, setEditLevel] = useState(1);

  useEffect(() => {
    if (password) {
      localStorage.setItem(ADMIN_PASSWORD_KEY, password);
    }
  }, [password]);

  // WHY: Fetch existing campaigns from server on mount so list persists across refreshes
  useEffect(() => {
    if (authenticated && password) {
      setLoadingCampaigns(true);
      setError(null);
      adminGetCampaigns(password)
        .then(setCampaigns)
        .catch((err) => setError(err.message))
        .finally(() => setLoadingCampaigns(false));
    }
  }, [authenticated, password]);

  const handleAuth = () => {
    setAuthenticated(true);
  };

  const handleCreateCampaign = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    setError(null);
    setCreatedSlug(null);

    try {
      const campaign = await adminCreateCampaign(password, newName.trim(), newLevel);
      setCampaigns([...campaigns, campaign]);
      setNewName('');
      setNewLevel(1);
      setCreatedSlug(campaign.slug);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCampaign = async (id: string) => {
    setLoading(true);
    setError(null);

    try {
      await adminUpdateCampaign(password, id, editName, editLevel);
      setCampaigns(campaigns.map((c) =>
        c.id === id ? { ...c, name: editName, level: editLevel } : c
      ));
      setEditId(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!confirm('Delete this campaign and all its characters?')) return;
    setLoading(true);
    setError(null);

    try {
      await adminDeleteCampaign(password, id);
      setCampaigns(campaigns.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="max-w-md mx-auto">
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Admin Login</h2>
          <input
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input mb-4"
          />
          <button onClick={handleAuth} className="btn btn-primary w-full">
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="heading text-2xl font-bold mb-6">Admin Panel</h2>

      {error && (
        <div className="bg-dnd-danger-bg border border-dnd-danger-border text-dnd-danger-text px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
          <h3 className="heading text-lg font-semibold mb-4">Create Campaign</h3>
        <div className="space-y-4">
          <div>
            <label className="label">Campaign Name</label>
            <input
              type="text"
              placeholder="e.g. Curse of Strahd"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">Starting Level</label>
            <input
              type="number"
              value={newLevel}
              onChange={(e) => setNewLevel(parseInt(e.target.value) || 1)}
              min="1"
              max="20"
              className="input w-32"
            />
          </div>
          <button
            onClick={handleCreateCampaign}
            disabled={loading || !newName.trim()}
            className="btn btn-primary"
          >
            {loading ? 'Creating...' : 'Create Campaign'}
          </button>
        </div>

        {createdSlug && (
          <div className="mt-4 p-4 bg-green-900/50 border border-green-500 rounded-lg">
            <p className="text-green-200 font-semibold mb-2">Campaign created!</p>
            <p className="text-dnd-muted text-sm mb-2">Share this link with your players:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-dnd-dark border border-dnd-border px-3 py-2 rounded-md text-dnd-accent">
                {window.location.origin}/{createdSlug}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${createdSlug}`)}
                className="btn btn-secondary text-sm"
              >
                Copy
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="card">
          <h3 className="heading text-lg font-semibold mb-4">Campaigns</h3>
        {loadingCampaigns ? (
          <p className="text-dnd-muted">Loading campaigns...</p>
        ) : campaigns.length === 0 ? (
          <p className="text-dnd-muted">No campaigns yet.</p>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="p-4 panel">
                {editId === campaign.id ? (
                  <div className="flex gap-4">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="input flex-1"
                    />
                    <input
                      type="number"
                      value={editLevel}
                      onChange={(e) => setEditLevel(parseInt(e.target.value) || 1)}
                      min="1"
                      max="20"
                      className="input w-24"
                    />
                    <button
                      onClick={() => handleUpdateCampaign(campaign.id)}
                      disabled={loading}
                      className="btn btn-gold"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditId(null)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold">{campaign.name}</div>
                      <div className="text-sm text-dnd-muted">
                        Level {campaign.level}
                      </div>
                      <div className="text-xs text-dnd-faint mt-1 flex items-center gap-2">
                        <span>Slug: {campaign.slug}</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${campaign.slug}`)}
                          className="text-dnd-accent hover:text-dnd-accent-hover"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditId(campaign.id);
                          setEditName(campaign.name);
                          setEditLevel(campaign.level);
                        }}
                        className="btn btn-secondary text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        disabled={loading}
                        className="btn btn-danger text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
